import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';
import ToolPageHeader from '../components/ToolPageHeader';
import axios from 'axios';
import { getBackendUrl } from '../utils/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Music, Search, Loader2, Radio, Info, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const SongInfoPage = () => {
  const { t, currentLang } = useLanguage();
  const [artist, setArtist] = useState('');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [songData, setSongData] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [copiedSection, setCopiedSection] = useState(null);

  const handleSearch = async () => {
    if (!artist.trim() || !title.trim()) {
      toast.error(t('fillArtistTitle') || 'Συμπληρώστε Καλλιτέχνη και Τίτλο');
      return;
    }

    setLoading(true);
    setSongData(null);

    try {
      const response = await axios.post(`${API}/song-info`, {
        artist: artist.trim(),
        title: title.trim()
      });

      setSongData(response.data);
      toast.success(t('infoFound') || 'Βρέθηκαν πληροφορίες!');
    } catch (error) {
      console.error('Song info error:', error);
      toast.error(t('searchError') || 'Σφάλμα αναζήτησης');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const copyToClipboard = async (text, section) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSection(section);
      toast.success('Αντιγράφηκε!');
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (err) {
      toast.error('Αποτυχία αντιγραφής');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f0a1a] via-[#1a1025] to-[#0f0a1a]">
      {/* SEO Meta Tags - Title inherited from index.html (Radio Solutions Apps) */}
      <Helmet>
        <meta name="description" content="Αναζήτηση πληροφοριών για τραγούδια: έτος κυκλοφορίας, δισκογραφική, συνθέτες και προτάσεις εκφώνησης για ραδιοφωνικούς παραγωγούς." />
        <meta name="keywords" content="πληροφορίες τραγουδιών, έτος κυκλοφορίας, song info, radio solutions, ραδιοφωνικό περιεχόμενο" />
        <link rel="canonical" href="https://radio-apps.online/song-info" />
      </Helmet>

      <ToolPageHeader 
        title={t('songInfoTitle') || 'Πληροφορίες Τραγουδιών'} 
        showHelp={true}
        onHelpClick={() => setShowHelp(true)}
      />

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Main Card */}
        <Card className="shadow-2xl border-2 border-purple-500/30 bg-[#1a1025]/80 backdrop-blur">
          <CardHeader className="bg-gradient-to-r from-purple-900/50 to-indigo-900/50 border-b border-purple-500/30">
            <CardTitle className="flex items-center gap-3 text-2xl text-white">
              <div className="p-2 bg-purple-600 rounded-lg">
                <Music className="w-6 h-6" />
              </div>
              🎵 {t('songInfoTitle') || 'Πληροφορίες Τραγουδιών'}
            </CardTitle>
            <CardDescription className="text-purple-200/80 text-base">
              {t('songInfoDesc') || 'Αναζήτηση πληροφοριών για οποιοδήποτε τραγούδι'}
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-6 space-y-6">
            {/* Search Form */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="artist" className="text-sm font-medium text-gray-200">
                  {t('artistLabel') || 'Καλλιτέχνης'} (Artist)
                </Label>
                <Input
                  id="artist"
                  type="text"
                  placeholder={t('exampleArtist') || 'π.χ. Queen'}
                  value={artist}
                  onChange={(e) => setArtist(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="mt-1 bg-[#0f0a1a] border-purple-500/30 text-white placeholder:text-gray-500"
                  disabled={loading}
                  autoComplete="off"
                  data-testid="artist-input"
                />
              </div>
              
              <div>
                <Label htmlFor="title" className="text-sm font-medium text-gray-200">
                  {t('titleLabel') || 'Τίτλος'} (Title)
                </Label>
                <Input
                  id="title"
                  type="text"
                  placeholder={t('exampleTitle') || 'π.χ. Bohemian Rhapsody'}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="mt-1 bg-[#0f0a1a] border-purple-500/30 text-white placeholder:text-gray-500"
                  disabled={loading}
                  autoComplete="off"
                  data-testid="title-input"
                />
              </div>
            </div>

            {/* Tip Box */}
            <div className="bg-purple-900/30 border border-purple-500/30 rounded-lg p-4">
              <p className="text-sm text-purple-200 flex items-start gap-2">
                <Info className="w-5 h-5 flex-shrink-0 mt-0.5 text-purple-400" />
                <span><strong>Tip:</strong> {t('tipLatinChars') || 'Χρησιμοποιήστε λατινικούς χαρακτήρες για καλύτερα αποτελέσματα'}</span>
              </p>
            </div>

            {/* Search Button */}
            <Button 
              onClick={handleSearch} 
              disabled={loading || !artist.trim() || !title.trim()}
              className="w-full h-12 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-lg font-semibold"
              data-testid="search-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  {t('searching') || 'Αναζήτηση...'}
                </>
              ) : (
                <>
                  <Search className="mr-2 h-5 w-5" />
                  {t('searchSongInfo') || 'Αναζήτηση Πληροφοριών'}
                </>
              )}
            </Button>

            {/* Results */}
            {songData && (
              <div className="space-y-6 mt-8">
                {/* Song Information with Album Cover */}
                <div className="bg-gradient-to-r from-purple-900/40 to-indigo-900/40 rounded-xl p-6 border border-purple-500/30">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-xl text-white flex items-center gap-2">
                      <Music className="w-5 h-5 text-purple-400" />
                      {songData.artist} - {songData.title}
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(songData.info, 'info')}
                      className="text-purple-300 hover:text-white"
                    >
                      {copiedSection === 'info' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  
                  <div className="flex gap-6">
                    {/* Album Cover Image */}
                    {songData.album_image && (
                      <div className="flex-shrink-0">
                        <img 
                          src={songData.album_image} 
                          alt={`${songData.artist} - Album Cover`}
                          className="w-40 h-40 rounded-xl shadow-2xl object-cover border-2 border-purple-500/30"
                        />
                      </div>
                    )}
                    
                    {/* Song Info Text */}
                    <div className="flex-1 prose prose-invert max-w-none text-gray-200 whitespace-pre-line leading-relaxed">
                      {songData.info}
                    </div>
                  </div>
                </div>

                {/* Radio Announcement Suggestion */}
                <div className="bg-gradient-to-r from-amber-900/30 to-orange-900/30 rounded-xl p-6 border-2 border-amber-500/40">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-xl text-amber-200 flex items-center gap-2">
                      <Radio className="w-5 h-5" />
                      💬 {t('radioAnnouncement') || 'Πρόταση Εκφώνησης'}
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(songData.radio_announcement, 'announcement')}
                      className="text-amber-300 hover:text-white"
                    >
                      {copiedSection === 'announcement' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  
                  <div className="bg-[#0f0a1a]/80 rounded-lg p-5 text-amber-100 italic text-lg border border-amber-500/30">
                    &ldquo;{songData.radio_announcement}&rdquo;
                  </div>
                  
                  <p className="text-sm text-amber-300/80 mt-3 flex items-center gap-2">
                    💡 {t('announcementTip') || 'Προσαρμόστε το κείμενο στο στυλ σας και χρησιμοποιήστε το στην εκπομπή σας'}
                  </p>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!songData && !loading && (
              <div className="text-center py-12">
                <Music className="w-20 h-20 mx-auto mb-4 text-purple-500/30" />
                <p className="text-gray-400 text-lg">{t('fillArtistTitleSearch') || 'Συμπληρώστε καλλιτέχνη και τίτλο για αναζήτηση'}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Feature Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          <div className="bg-[#1a1025]/60 rounded-xl p-5 border border-purple-500/20">
            <div className="text-3xl mb-2">📅</div>
            <h4 className="font-semibold text-white mb-1">Έτος Κυκλοφορίας</h4>
            <p className="text-sm text-gray-400">Πληροφορίες για την ημερομηνία και το album</p>
          </div>
          <div className="bg-[#1a1025]/60 rounded-xl p-5 border border-purple-500/20">
            <div className="text-3xl mb-2">✍️</div>
            <h4 className="font-semibold text-white mb-1">Συνθέτες & Στιχουργοί</h4>
            <p className="text-sm text-gray-400">Ποιοι δημιούργησαν το τραγούδι</p>
          </div>
          <div className="bg-[#1a1025]/60 rounded-xl p-5 border border-purple-500/20">
            <div className="text-3xl mb-2">🎙️</div>
            <h4 className="font-semibold text-white mb-1">Πρόταση Εκφώνησης</h4>
            <p className="text-sm text-gray-400">Έτοιμο κείμενο για τον παραγωγό</p>
          </div>
        </div>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4"
          onClick={() => setShowHelp(false)}
        >
          <div 
            className="bg-[#1a1025] rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-auto border-2 border-purple-500/30"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-5 sticky top-0">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Info className="w-6 h-6" />
                Οδηγίες Χρήσης
              </h2>
            </div>
            <div className="p-6 space-y-4 text-gray-200">
              <div>
                <h3 className="font-semibold text-purple-300 mb-2">🎵 Τι κάνει αυτό το εργαλείο;</h3>
                <p>Αναζητά πληροφορίες για οποιοδήποτε τραγούδι: έτος κυκλοφορίας, δισκογραφική εταιρεία, συνθέτες, στιχουργούς και προτείνει κείμενα εκφώνησης για ραδιοφωνικούς παραγωγούς.</p>
              </div>
              <div>
                <h3 className="font-semibold text-purple-300 mb-2">📝 Πώς το χρησιμοποιώ;</h3>
                <ol className="list-decimal list-inside space-y-1 text-gray-300">
                  <li>Γράψτε τον καλλιτέχνη (π.χ. Queen)</li>
                  <li>Γράψτε τον τίτλο (π.χ. Bohemian Rhapsody)</li>
                  <li>Πατήστε &ldquo;Αναζήτηση Πληροφοριών&rdquo;</li>
                  <li>Χρησιμοποιήστε το κουμπί αντιγραφής για να πάρετε το κείμενο</li>
                </ol>
              </div>
              <div>
                <h3 className="font-semibold text-amber-300 mb-2">💡 Tips για καλύτερα αποτελέσματα:</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-300">
                  <li>Χρησιμοποιήστε λατινικούς χαρακτήρες</li>
                  <li>Γράψτε το σωστό όνομα καλλιτέχνη (όχι ψευδώνυμα)</li>
                  <li>Αποφύγετε ειδικούς χαρακτήρες</li>
                </ul>
              </div>
              <Button 
                onClick={() => setShowHelp(false)}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                Κλείσιμο
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SongInfoPage;
